// App root component - orchestrates all providers
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { VisibilityProvider, useVisibility } from "@/contexts/VisibilityContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { AnimatedRoutes } from "./components/layout/AnimatedRoutes";
import { NavigationProgress } from "./components/ui/navigation-progress";
import { ScrollToTop } from "./components/ui/scroll-to-top";
import { CommandPalette } from "./components/ui/command-palette";
import { KeyboardShortcutsModal } from "./components/ui/keyboard-shortcuts-modal";
import { ChatJCI } from "./components/chat/ChatJCI";
import { StructuredData } from "./components/seo/StructuredData";
import { AdminStatusBadge } from "./components/admin/AdminStatusBadge";
import { PublicModeIndicator } from "./components/admin/PublicModeIndicator";
import { OnboardingWizard } from "./components/onboarding/OnboardingWizard";
import { CookieConsentBanner } from "./components/gdpr/CookieConsentBanner";

const queryClient = new QueryClient();

function AppContent() {
  const { user, profile, loading, isAdmin } = useAuth();
  const { getVisibility } = useVisibility();

  // Check if ChatJCI is enabled
  const chatJCIVisibility = getVisibility('global-features.chat-jci');
  const shouldShowChatJCI = () => {
    if (!user) return false;
    if (chatJCIVisibility === 'hidden') return false;
    if (chatJCIVisibility === 'admin') return isAdmin;
    return true; // 'public' or 'member' - show for authenticated users
  };

  // Show onboarding wizard for logged-in users who haven't completed onboarding
  const showOnboarding = !loading && user && profile && profile.onboarding_completed === false;

  if (showOnboarding) {
    return <OnboardingWizard />;
  }

  return (
    <>
      <NavigationProgress />
      <StructuredData />
      <AnimatedRoutes />
      <ScrollToTop />
      <CommandPalette />
      <KeyboardShortcutsModal />
      {shouldShowChatJCI() && <ChatJCI />}
      <AdminStatusBadge />
      <PublicModeIndicator />
    </>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <LanguageProvider>
            <CookieConsentProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AuthProvider>
                  <VisibilityProvider>
                    <ErrorBoundary>
                      <AppContent />
                      <CookieConsentBanner />
                    </ErrorBoundary>
                  </VisibilityProvider>
                </AuthProvider>
              </BrowserRouter>
            </CookieConsentProvider>
          </LanguageProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

